import { useCallback, useEffect, useRef } from "react";
import notificationService, {
  MessageKind,
} from "@blacknut/javascript-sdk/dist/ws/PushNotificationService";
import { useAppContext } from "./ContextProvider";
import { NotificationMessage } from "@blacknut/javascript-sdk/dist/ws/NotificationService";
import {
  RemoteNotification,
  RemoteNotificationTrigger,
} from "@blacknut/javascript-sdk/dist/model/RemoteNotification";
import { useTranslation } from "react-i18next";
import { usePlayerContext } from "./PlayerContext";
import { PlayerStatus } from "./PlayerUtils";
import {
  Button,
  useCustomBack,
  MessageModal,
  useModal,
  ModalSubscription,
} from "@blacknut/react-sdk/dist";
import React from "react";
interface UsePushNotificationsOpts {
  onStop: () => void;
}
const usePushNotifications = ({ onStop }: UsePushNotificationsOpts) => {
  const { sdkConfig } = useAppContext();
  const { playerStatus } = usePlayerContext();
  const apiEndPoint = sdkConfig?.endpoint;
  let wsEndPoint = "wss://ws.blacknut.com/api/v1/ws/notifications";
  if (apiEndPoint?.match("dev")) {
    wsEndPoint = "wss://dev-ws.blacknut.net/api/v1/ws/notifications";
  } else if (apiEndPoint?.match("staging")) {
    wsEndPoint = "wss://staging-ws.blacknut.net/api/v1/ws/notifications";
  }
  notificationService.endpoint = wsEndPoint;
  const { push: modalPush } = useModal();
  const { t } = useTranslation();

  const { push: backPush } = useCustomBack();

  // FIX : https://blacknut.atlassian.net/browse/CA-2598
  // Since popup is closed within callback, the reference to the modalPush fn is still the same
  const modalPushRef = useRef(modalPush);
  const modalSubscription = useRef<ModalSubscription>();

  const closeModal = useCallback(() => {
    if (modalSubscription.current) {
      modalSubscription.current.remove();
      modalSubscription.current = undefined;
    }
  }, []);

  useEffect(() => {
    modalPushRef.current = modalPush;
  }, [modalPush]);

  const handleIdleUserError = useCallback(
    (notification: RemoteNotification) => {
      if (playerStatus !== PlayerStatus.CONNECTED) {
        return;
      }

      closeModal();

      setTimeout(() => {
        modalSubscription.current = modalPushRef.current((props) => (
          <MessageModal
            {...props}
            title={notification.title}
            message={notification.description}
            onClose={closeModal}
            buttons={
              <Button
                key="ok"
                onClick={() => {
                  closeModal();
                  onStop();
                }}
              >
                {t("buttons.ok")}
              </Button>
            }
          />
        ));
      }, 500); // wait for reference to be updated
    },
    [onStop, closeModal, playerStatus, t],
  );

  const handleIdleUserWarn = useCallback(
    (notification: RemoteNotification) => {
      if (playerStatus !== PlayerStatus.CONNECTED) {
        return;
      }

      closeModal();

      setTimeout(() => {
        modalSubscription.current = modalPushRef.current((props) => (
          <MessageModal
            {...props}
            title={notification.title}
            message={notification.description}
            onClose={closeModal}
            buttons={
              <Button key="ok" onClick={closeModal}>
                {t("buttons.ok")}
              </Button>
            }
          />
        ));
      }, 500); // wait for reference to be updated
    },
    [closeModal, playerStatus, t],
  );

  useEffect(() => {
    if (playerStatus !== PlayerStatus.CONNECTED) {
      return;
    }
    const sub = notificationService.onMessage().subscribe((n: NotificationMessage) => {
      if (n.kind !== MessageKind.NOTIFICATION_RESP) return;
      const { notification } = n.payload as any;
      if (notification.trigger === RemoteNotificationTrigger.IDLE_USER_ERROR) {
        handleIdleUserError(notification);
      } else if (notification.trigger === RemoteNotificationTrigger.IDLE_USER_WARN) {
        handleIdleUserWarn(notification);
      }
    });
    notificationService.connect();
    return () => {
      notificationService.disconnect();
      sub.unsubscribe();
    };
  }, [handleIdleUserWarn, handleIdleUserError, playerStatus]);
};
export default usePushNotifications;
